// ---------------------------------------------------------------------------
// useDatasetUsage — per-dataset usage rows for the Overview dashboard, backed
// by TanStack Query.
//
// Reads through the shared [[datasetUsageQuery]] definition using the DataHub
// client. The usage API is now available to any caller with at least read
// access (and to public datasets), so the fetch always runs and the API is the
// authority on access — a residual `403` for a caller without access surfaces
// through `error`. A `select` transform exposes just the records array while
// the cache still stores the raw server result.
// ---------------------------------------------------------------------------

import { useQuery } from '@tanstack/react-query';

import { AppError } from '../../../api/errors.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import type {
  DatasetUsageRecord,
  DatasetUsageResult,
} from '../../../services/dataHubService.interface.js';

import { datasetUsageQuery } from './datasetUsageQueries.js';

export interface UseDatasetUsageResult {
  records: DatasetUsageRecord[];
  isLoading: boolean;
  error: AppError | null;
}

function toAppError(error: Error | null): AppError | null {
  if (error === null) return null;
  return error instanceof AppError
    ? error
    : new AppError(error.message, 'UNKNOWN');
}

export function useDatasetUsage(datasetId: string): UseDatasetUsageResult {
  const client = useDataHubClient();

  const query = useQuery({
    ...datasetUsageQuery(client, datasetId),
    select: (data: DatasetUsageResult): DatasetUsageRecord[] => data.records,
  });

  return {
    records: query.data ?? [],
    isLoading: query.isLoading,
    error: toAppError(query.error),
  };
}
